import { Chip, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import {
  EventBusy as EventBusyIcon,
  ForwardToInbox as ForwardToInboxIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import type { User } from '@/types'
import { getCabinetInvitationState } from '@/utils/cabinetInvitation'
import { formatDateDMY } from '@/utils/date'

interface CabinetStatusChipProps {
  user: Pick<User, 'has_cabinet' | 'cabinet_invitation'>
  sx?: SxProps<Theme>
}

/**
 * Отметка рядом с именем ученика без кабинета: «Без кабинета»,
 * «Приглашён в кабинет» или «Приглашение истекло». В подсказке — почта
 * приглашения и срок ссылки.
 *
 * Ученик без кабинета в EasyA не входит — почты и пароля у него нет, его ведёт
 * учитель. У ученика с кабинетом отметки нет: это обычное состояние, и чип на
 * каждой строке списка только отвлекал бы.
 */
export function CabinetStatusChip({ user, sx }: CabinetStatusChipProps) {
  const { t } = useTranslation()
  const state = getCabinetInvitationState(user)

  if (state === 'has_cabinet') {
    return null
  }

  const invitation = user.cabinet_invitation
  const params = {
    email: invitation?.email ?? '—',
    date: formatDateDMY(invitation?.expires_at),
  }

  const view = {
    none: { label: t('users.noCabinet'), hint: t('users.noCabinetHint'), icon: undefined },
    invited: {
      label: t('users.cabinetInvitation.chipInvited'),
      hint: t('users.cabinetInvitation.chipInvitedHint', params),
      icon: <ForwardToInboxIcon />,
    },
    expired: {
      label: t('users.cabinetInvitation.chipExpired'),
      hint: t('users.cabinetInvitation.chipExpiredHint', params),
      icon: <EventBusyIcon />,
    },
  }[state]

  return (
    <Tooltip title={view.hint} arrow enterTouchDelay={0}>
      <Chip
        size="small"
        variant="outlined"
        color={state === 'expired' ? 'warning' : 'default'}
        icon={view.icon}
        label={view.label}
        sx={sx}
      />
    </Tooltip>
  )
}
